import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import DashboardShell from './components/DashboardShell';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function buildUrl(path) {
  return API_BASE + (path.startsWith('/') ? path : '/' + path);
}

async function apiRequest(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(buildUrl(path), {
    ...options,
    headers: {
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || 'Request failed (' + response.status + ')');
  }
  return data;
}

function formatDate(value) {
  if (!value) return '—';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value;
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function SummaryCard({ label, value, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    amber: 'bg-amber-100 text-amber-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className={'inline-flex rounded-lg px-2.5 py-1 text-xs font-semibold ' + tones[tone]}>
        {label}
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value ?? 0}</p>
    </div>
  );
}

function ActivityBadge({ type, label }) {
  const styles = {
    filed: 'bg-blue-100 text-blue-800',
    served: 'bg-amber-100 text-amber-800',
    garnishment: 'bg-red-100 text-red-800',
  };
  return (
    <span className={'rounded-full px-2.5 py-1 text-xs font-semibold ' + (styles[type] || styles.filed)}>
      {label}
    </span>
  );
}

export default function ProspectLawsuitReportPage() {
  const [data, setData] = useState(null);
  const [activityType, setActivityType] = useState('all');
  const [outreachStatus, setOutreachStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [workingTodoId, setWorkingTodoId] = useState('');
  const [copied, setCopied] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const loadReport = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams({
        activity_type: activityType,
        outreach_status: outreachStatus,
      });
      if (search) query.set('search', search);
      const result = await apiRequest('/firm/prospect-lawsuit-report/?' + query.toString());
      setData(result);
      return result;
    } catch (requestError) {
      setError(requestError.message || 'Could not load the prospect lawsuit report.');
      return null;
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [activityType, outreachStatus, search]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const runScan = async () => {
    setScanning(true);
    setError('');
    setNotice('');
    const requestedAt = Date.now();
    try {
      await apiRequest('/firm/prospect-lawsuit-report/', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setNotice('The scan is running. This page will update when it finishes.');

      for (let attempt = 0; attempt < 45; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 4000));
        const refreshed = await loadReport(true);
        const latest = refreshed?.latest_scan;
        const isThisScan = latest?.started_at
          && new Date(latest.started_at).getTime() >= requestedAt - 5000;
        if (isThisScan && latest.status !== 'running') {
          setNotice(
            latest.status === 'succeeded'
              ? 'Scan complete. New emails and text ToDos have been created.'
              : 'Scan finished with issues. Review the status below and Automation Health for details.',
          );
          break;
        }
      }
    } catch (requestError) {
      if (requestError.message.includes('already running')) {
        setNotice(requestError.message);
      } else {
        setError(requestError.message || 'Could not start the scan.');
      }
    } finally {
      setScanning(false);
      loadReport(true);
    }
  };

  const copyValue = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied(''), 1800);
    } catch (_) {
      window.prompt('Copy this:', value);
    }
  };

  const markTextSent = async (activity) => {
    if (!activity.todo?.id) return;
    setWorkingTodoId(activity.todo.id);
    setError('');
    try {
      await apiRequest('/firm/todays-todos/' + activity.todo.id + '/complete/', {
        method: 'POST',
        body: JSON.stringify({ outcome: 'completed' }),
      });
      setNotice('Text marked sent and added to communications history.');
      await loadReport(true);
    } catch (requestError) {
      setError(requestError.message || 'Could not mark the text sent.');
    } finally {
      setWorkingTodoId('');
    }
  };

  const summary = data?.summary || {};
  const latestScan = data?.latest_scan;

  return (
    <DashboardShell>
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-7 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-4 rounded-2xl bg-gradient-to-r from-slate-900 to-indigo-900 p-6 text-white shadow-lg md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <ScaleIcon className="h-8 w-8" />
              <h1 className="text-2xl font-bold">Prospect Lawsuit Report</h1>
            </div>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-indigo-100">
              High-confidence Public Digital matches require both the debtor&apos;s name and a
              current or prior address. A new milestone sends one tracked email and creates one
              text ToDo; repeat scans do not resend it.
            </p>
          </div>
          <button
            type="button"
            onClick={runScan}
            disabled={scanning}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-indigo-800 shadow-sm hover:bg-indigo-50 disabled:opacity-60"
          >
            <ArrowPathIcon className={'h-5 w-5 ' + (scanning ? 'animate-spin' : '')} />
            {scanning ? 'Scanning…' : 'Run scan now'}
          </button>
        </header>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-800">
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm font-medium text-indigo-800">
            {notice}
          </div>
        )}

        <section className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
          <SummaryCard label="All matches" value={summary.total} />
          <SummaryCard label="Filed" value={summary.filed} tone="indigo" />
          <SummaryCard label="Served" value={summary.served} tone="amber" />
          <SummaryCard label="Garnishments" value={summary.garnishments} tone="red" />
          <SummaryCard label="Texts needed" value={summary.needs_text} tone="amber" />
          <SummaryCard label="Contacted" value={summary.contacted} tone="green" />
          <SummaryCard label="Email failures" value={summary.email_failed} tone="red" />
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <label className="flex-1 text-sm font-medium text-slate-700">
              Search
              <div className="mt-1 flex rounded-lg border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-indigo-500">
                <MagnifyingGlassIcon className="ml-3 h-5 w-5 self-center text-slate-400" />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') setSearch(searchInput.trim());
                  }}
                  placeholder="Prospect, plaintiff, or public case number"
                  className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setSearch(searchInput.trim())}
                  className="border-l border-slate-200 px-4 text-sm font-semibold text-indigo-700"
                >
                  Search
                </button>
              </div>
            </label>
            <label className="text-sm font-medium text-slate-700">
              Activity
              <select
                value={activityType}
                onChange={(event) => setActivityType(event.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2"
              >
                <option value="all">All activity</option>
                <option value="filed">Lawsuit filed</option>
                <option value="served">Lawsuit served</option>
                <option value="garnishment">Garnishment</option>
              </select>
            </label>
            <label className="text-sm font-medium text-slate-700">
              Outreach
              <select
                value={outreachStatus}
                onChange={(event) => setOutreachStatus(event.target.value)}
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2"
              >
                <option value="all">All outreach</option>
                <option value="needs_text">Text still needed</option>
                <option value="contacted">Text sent</option>
                <option value="email_failed">Email failed</option>
              </select>
            </label>
          </div>

          <div className="mt-4 border-t border-slate-100 pt-3 text-sm text-slate-600">
            {latestScan ? (
              <span>
                <strong>Last scan:</strong> {formatDateTime(latestScan.started_at)} ·{' '}
                <span className={latestScan.status === 'succeeded' ? 'text-green-700' : latestScan.status === 'running' ? 'text-indigo-700' : 'text-red-700'}>
                  {latestScan.status_display}
                </span>
                {latestScan.summary ? ' · ' + latestScan.summary : ''}
              </span>
            ) : (
              <span>No scan has run yet.</span>
            )}
          </div>
        </section>

        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-slate-600">
            Loading report…
          </div>
        ) : (data?.activities || []).length === 0 ? (
          <div className="rounded-xl border border-green-200 bg-green-50 p-12 text-center">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-600" />
            <h2 className="mt-3 text-lg font-semibold text-green-900">No matching activity</h2>
            <p className="mt-1 text-sm text-green-700">
              There are no saved events matching these filters.
            </p>
          </div>
        ) : (
          <section className="space-y-4">
            {data.activities.map((activity) => {
              const todoOpen = activity.todo?.status === 'open';
              const emailFailed = activity.email?.status === 'failed';
              return (
                <article key={activity.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-5 xl:flex-row xl:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <ActivityBadge type={activity.activity_type} label={activity.activity_type_display} />
                        <span className="text-sm font-semibold text-slate-700">
                          {formatDate(activity.activity_date)}
                        </span>
                        {todoOpen ? (
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                            Text needed
                          </span>
                        ) : (
                          <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                            Text sent
                          </span>
                        )}
                      </div>

                      <h2 className="mt-3 text-lg font-bold text-slate-900">
                        {activity.prospect_case.name}
                      </h2>
                      <p className="mt-1 text-sm text-slate-600">
                        Prospect created {formatDate(activity.prospect_case.created_at)}
                      </p>

                      <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                        <div><dt className="font-semibold text-slate-500">Public case</dt><dd className="mt-0.5 text-slate-900">{activity.public_case_number}</dd></div>
                        <div><dt className="font-semibold text-slate-500">Plaintiff</dt><dd className="mt-0.5 text-slate-900">{activity.plaintiff || '—'}</dd></div>
                        <div><dt className="font-semibold text-slate-500">Defendant</dt><dd className="mt-0.5 text-slate-900">{activity.defendant || '—'}</dd></div>
                        <div><dt className="font-semibold text-slate-500">County</dt><dd className="mt-0.5 capitalize text-slate-900">{activity.county || '—'}</dd></div>
                        <div><dt className="font-semibold text-slate-500">Amount</dt><dd className="mt-0.5 text-slate-900">{formatMoney(activity.amount)}</dd></div>
                        {activity.employer_name && (
                          <div><dt className="font-semibold text-slate-500">Employer</dt><dd className="mt-0.5 text-slate-900">{activity.employer_name}</dd></div>
                        )}
                        <div>
                          <dt className="font-semibold text-slate-500">Match confidence</dt>
                          <dd className="mt-0.5 text-slate-900">
                            Name {Math.round(activity.match.name_score * 100)}% · Address {Math.round(activity.match.address_score * 100)}%
                          </dd>
                        </div>
                      </dl>

                      <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                        <p><strong>Matched prospect address:</strong> {activity.match.prospect_address}</p>
                        <p className="mt-1"><strong>Public record address:</strong> {activity.match.public_address}</p>
                      </div>

                      {activity.todo?.copy_text && (
                        <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/40 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                              Copyable text · {activity.todo.employee?.full_name || 'Unassigned'}
                            </span>
                            <button
                              type="button"
                              onClick={() => copyValue(activity.todo.copy_text, 'text-' + activity.id)}
                              className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-700"
                            >
                              <ClipboardDocumentIcon className="h-4 w-4" />
                              {copied === 'text-' + activity.id ? 'Copied' : 'Copy'}
                            </button>
                          </div>
                          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-800">{activity.todo.copy_text}</p>
                        </div>
                      )}
                    </div>

                    <aside className="w-full space-y-3 xl:w-72">
                      <div className={'rounded-lg border p-3 text-sm ' + (emailFailed ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800')}>
                        <div className="flex items-center gap-2 font-semibold">
                          {emailFailed ? <ExclamationTriangleIcon className="h-5 w-5" /> : <EnvelopeIcon className="h-5 w-5" />}
                          {activity.email
                            ? 'Email: ' + activity.email.status_display
                            : activity.prospect_case.emails.length
                              ? 'Email pending'
                              : 'No prospect email available'}
                        </div>
                        {activity.email?.sent_at && <p className="mt-1">Sent {formatDateTime(activity.email.sent_at)}</p>}
                        {activity.email?.error_message && <p className="mt-1 break-words">{activity.email.error_message}</p>}
                      </div>

                      {activity.prospect_case.phones.map((phone, index) => (
                        <div key={phone + '-' + index} className="flex overflow-hidden rounded-lg border border-slate-300">
                          <a href={'sms:' + phone} className="inline-flex flex-1 items-center gap-2 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
                            <PhoneIcon className="h-4 w-4" /> {phone}
                          </a>
                          <button
                            type="button"
                            onClick={() => copyValue(phone, 'phone-' + activity.id + '-' + index)}
                            className="border-l border-slate-300 px-3 text-indigo-700 hover:bg-indigo-50"
                            aria-label={'Copy ' + phone}
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}

                      {activity.public_url && (
                        <a href={activity.public_url} target="_blank" rel="noopener noreferrer" className="block rounded-lg border border-indigo-300 px-4 py-2 text-center text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
                          Open public record
                        </a>
                      )}
                      <a href={'/case/' + activity.prospect_case.case_id} className="block rounded-lg border border-slate-300 px-4 py-2 text-center text-sm font-semibold text-slate-700 hover:bg-slate-50">
                        Open prospect case
                      </a>
                      {todoOpen && (
                        <button
                          type="button"
                          onClick={() => markTextSent(activity)}
                          disabled={workingTodoId === activity.todo.id}
                          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                          {workingTodoId === activity.todo.id ? 'Saving…' : 'Mark text sent'}
                        </button>
                      )}
                    </aside>
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </main>
    </DashboardShell>
  );
}
